import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";

const EditManagerDialog = ({
  open,
  onClose,
  employee,
  currentManager,
  currentManagerEmail,
  employees,
  refetch,
  hasHrmsIntegration = false,
}) => {
  const [updateEmployee, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);

  useEffect(() => {
    if (open && employees.length > 0) {
      // Handle manager as string or array
      const managerName = Array.isArray(currentManager)
        ? currentManager[0]
        : currentManager;

      if (!managerName || managerName === "Not Assigned" || managerName === "-") {
        setSelectedManager({ id: "no_manager", name: "No Manager", email: "", employee_id: null });
      } else {
        // First try to find by email (most reliable)
        let manager = null;
        if (currentManagerEmail) {
          manager = employees.find(
            (emp) => emp.emails?.[0]?.email === currentManagerEmail || emp.work_email === currentManagerEmail
          );
        }

        if (!manager && employee?.manager?.id) {
          manager = employees.find(
            (emp) => emp.id === employee.manager.id ||
              emp.employee_id === employee.manager.id ||
              String(emp.id) === String(employee.manager.id) ||
              String(emp.employee_id) === String(employee.manager.id)
          );
        }

        if (!manager && managerName) {
          manager = employees.find(
            (emp) => emp.name?.toLowerCase() === managerName.toLowerCase()
          );
        }

        if (manager) {
          setSelectedManager({
            id: manager.id,
            name: manager.name,
            email: manager.emails?.[0]?.email || manager.work_email || "",
            employee_id: manager.employee_id,
          });
        } else {
          setSelectedManager({ id: "no_manager", name: "No Manager", email: "", employee_id: null });
        }
      }
    } else if (open && employees.length === 0) {
      setSelectedManager({ id: "no_manager", name: "No Manager", email: "", employee_id: null });
    }
  }, [open, currentManager, currentManagerEmail, employees, employee]);

  const handleSave = async () => {
    if (!employee?.id) return;

    try {
      const employeeEmail = Array.isArray(employee.emails)
        ? employee.emails.find((e) => e.type === "work")?.email || employee.emails[0]?.email || employee.work_email || ""
        : employee.work_email || "";

      if (!employeeEmail) {
        setSnackbarInfo({
          message: "Employee email is required but not found",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      let managerValue = null;

      if (selectedManager && selectedManager.id !== "no_manager") {
        const managerId = selectedManager.employee_id || selectedManager.id?.toString();
        managerValue = {
          id: managerId,
        };
      }

      const response = await updateEmployee({
        variables: {
          input: [
            {
              id: employee.id,
              email: employeeEmail,
              manager: managerValue,
            },
          ],
        },
      });

      if (response?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: "Manager updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) {
          await refetch();
        }
        onClose();
      } else {
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to update manager",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update manager",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const currentEmployeeEmail = Array.isArray(employee?.emails)
    ? employee.emails.find((e) => e.type === "work")?.email || employee.emails[0]?.email || employee?.work_email || ""
    : employee?.work_email || "";

  const employeeOptions = [
    {
      id: "no_manager",
      name: "No Manager",
      email: "",
      employee_id: null,
    },
    ...employees
      .filter((emp) => {
        if (emp.id === employee?.id) return false;
        const empEmail = emp.emails?.[0]?.email || emp.work_email || "";
        if (currentEmployeeEmail && empEmail && empEmail.toLowerCase() === currentEmployeeEmail.toLowerCase()) {
          return false;
        }
        // Only show in-scope employees
        return emp.in_scope === true;
      })
      .map((emp) => ({
        id: emp.id,
        name: emp.name,
        email: emp.emails?.[0]?.email || "",
        employee_id: emp.employee_id,
      })),
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Edit Reporting Manager
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {hasHrmsIntegration && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Reporting Manager is managed by HRMS integration and cannot be edited manually.
              </Alert>
            )}
            <Autocomplete
              options={employeeOptions}
              getOptionLabel={(option) => {
                if (option.id === null) {
                  return option.name;
                }
                return option.name;
              }}
              value={selectedManager ? employeeOptions.find(opt => opt.id === selectedManager.id) || null : null}
              onChange={(event, newValue) => {
                if (!newValue) {
                  setSelectedManager({ id: "no_manager", name: "No Manager", email: "", employee_id: null });
                } else if (newValue.id === "no_manager") {
                  setSelectedManager({ id: "no_manager", name: "No Manager", email: "", employee_id: null });
                } else {
                  const foundEmployee = employees.find(emp => emp.id === newValue.id || emp.employee_id === newValue.employee_id);
                  if (foundEmployee) {
                    setSelectedManager({
                      id: foundEmployee.id,
                      name: foundEmployee.name,
                      email: foundEmployee.emails?.[0]?.email || "",
                      employee_id: foundEmployee.employee_id,
                    });
                  } else {
                    setSelectedManager(newValue);
                  }
                }
              }}
              disabled={updating || hasHrmsIntegration}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Manager"
                  placeholder="Search for a manager..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || hasHrmsIntegration}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditManagerDialog;

